'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from './ui/button';
import { Check, Crown, Sparkles, Zap } from 'lucide-react';
import { PRICING } from '@/contexts/PremiumContext';
import { useState } from 'react';
import { useRouter } from 'next/navigation';

interface PremiumUpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PremiumUpgradeDialog({ open, onOpenChange }: PremiumUpgradeDialogProps) {
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('yearly');
  const router = useRouter();

  const handleUpgrade = () => {
    onOpenChange(false);
    router.push('/premium');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-[#0a192f] border-2 border-[#233554] dark:bg-[#0a192f] light:bg-white">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-center flex items-center justify-center gap-3">
            <Crown className="w-8 h-8 text-[#FFD700]" />
            <span className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] bg-clip-text text-transparent">
              Elementium Premium
            </span>
            <Sparkles className="w-8 h-8 text-[#FFD700]" />
          </DialogTitle>
          <DialogDescription className="text-center text-lg text-[#a8b2d1] dark:text-[#a8b2d1] light:text-gray-600">
            Débloquez toutes les fonctionnalités avancées
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 mt-6">
          {/* Plan Mensuel */}
          <div
            className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all ${
              selectedPlan === 'monthly'
                ? 'border-[#64ffda] bg-[#64ffda]/10'
                : 'border-[#233554] hover:border-[#64ffda]/50'
            }`}
            onClick={() => setSelectedPlan('monthly')}
          >
            <div className="text-center mb-4">
              <h3 className="text-xl font-semibold text-white dark:text-white light:text-gray-900">Mensuel</h3>
              <div className="mt-2">
                <span className="text-4xl font-bold text-[#64ffda]">{PRICING.monthly.price}{PRICING.monthly.currency}</span>
                <span className="text-[#a8b2d1] dark:text-[#a8b2d1] light:text-gray-600">/{PRICING.monthly.period}</span>
              </div>
            </div>
            <p className="text-sm text-center text-[#a8b2d1] dark:text-[#a8b2d1] light:text-gray-600">Facturation mensuelle</p>
          </div>

          {/* Plan Annuel - Recommandé */}
          <div
            className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all ${
              selectedPlan === 'yearly'
                ? 'border-[#FFD700] bg-gradient-to-br from-[#FFD700]/20 to-[#FFA500]/20'
                : 'border-[#233554] hover:border-[#FFD700]/50'
            }`}
            onClick={() => setSelectedPlan('yearly')}
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#0a192f] px-4 py-1 rounded-full text-xs font-bold">
              ÉCONOMISEZ {PRICING.yearly.savings}
            </div>
            <div className="text-center mb-4">
              <h3 className="text-xl font-semibold text-white dark:text-white light:text-gray-900">Annuel</h3>
              <div className="mt-2">
                <span className="text-4xl font-bold text-[#FFD700]">{PRICING.yearly.price}{PRICING.yearly.currency}</span>
                <span className="text-[#a8b2d1] dark:text-[#a8b2d1] light:text-gray-600">/{PRICING.yearly.period}</span>
              </div>
              <p className="text-xs text-[#64ffda] mt-1">Soit 2,08€/mois</p>
            </div>
            <p className="text-sm text-center text-[#a8b2d1] dark:text-[#a8b2d1] light:text-gray-600">Facturation annuelle</p>
          </div>
        </div>

        {/* Features List */}
        <div className="mt-6 space-y-3">
          <h4 className="font-semibold text-[#64ffda] flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Fonctionnalités Premium
          </h4>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              'Visualisation 3D de tous les 118 éléments',
              'Zéro publicité',
              'Quiz interactifs et mode révision',
              'Export PDF des fiches d\'éléments',
              'Export CSV et JSON des données',
              'Statistiques d\'apprentissage',
              'Mises à jour prioritaires',
              'Support client prioritaire',
            ].map((feature, index) => (
              <div key={index} className="flex items-start gap-2">
                <Check className="w-5 h-5 text-[#64ffda] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#a8b2d1] dark:text-[#a8b2d1] light:text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="mt-6 flex flex-col gap-3">
          <Button
            onClick={handleUpgrade}
            className="w-full bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#0a192f] hover:opacity-90 font-bold text-lg py-6"
          >
            <Crown className="w-5 h-5 mr-2" />
            Activer Premium - {selectedPlan === 'monthly' ? `${PRICING.monthly.price}€/mois` : `${PRICING.yearly.price}€/an`}
          </Button>
          <p className="text-xs text-center text-[#a8b2d1] dark:text-[#a8b2d1] light:text-gray-500">
            🔒 Paiement sécurisé • Annulation à tout moment • Garantie satisfait ou remboursé 30 jours
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
